import React, { useState, useRef } from 'react';
import { Play, FileVideo, Pause } from 'lucide-react';

const isYouTube = (url) => /(?:youtube\.com|youtu\.be)/.test(url);
const isVimeo   = (url) => /vimeo\.com/.test(url);

const toEmbedUrl = (url) => {
  if (isYouTube(url)) {
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&modestbranding=1&rel=0` : url;
  }
  if (isVimeo(url)) {
    const m = url.match(/vimeo\.com\/(\d+)/);
    return m ? `https://player.vimeo.com/video/${m[1]}?autoplay=1` : url;
  }
  return url;
};

/**
 * VideoPlaceholder
 *  Props:
 *   - number, title, lengthSec, scriptSummary, timeline (array)
 *   - videoUrl (optional) — MP4/WebM URL, or YouTube/Vimeo page URL
 *   - posterUrl (optional) — thumbnail shown before play
 *   - theme ('light' | 'dark')
 *
 * If videoUrl is provided: renders a real <video> player (or YouTube/Vimeo iframe).
 * If not: renders the script + timeline placeholder so the site looks complete
 * while videos are still in production.
 */
export const VideoPlaceholder = ({
  number, title, lengthSec, scriptSummary, timeline = [],
  videoUrl, posterUrl, theme = 'light',
}) => {
  const dark = theme === 'dark';
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const hasVideo = Boolean(videoUrl);
  const isEmbed = hasVideo && (isYouTube(videoUrl) || isVimeo(videoUrl));

  const handlePlay = () => {
    if (!hasVideo) return;
    if (isEmbed) { setPlaying(true); return; }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  return (
    <div
      className={`rounded-2xl border ${dark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'} overflow-hidden`}
      data-testid={`v2-video-${number}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Video frame */}
        <div className="relative aspect-video md:aspect-auto bg-gradient-to-br from-slate-900 to-slate-700 overflow-hidden">
          {/* Background dot grid (placeholder only) */}
          {!hasVideo && (
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '24px 24px'
            }} />
          )}

          {/* Real video — direct MP4 */}
          {hasVideo && !isEmbed && (
            <video
              ref={videoRef}
              src={videoUrl}
              poster={posterUrl || undefined}
              controls
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover bg-black"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              data-testid={`v2-video-player-${number}`}
            />
          )}

          {/* Real video — embed (YouTube / Vimeo) */}
          {hasVideo && isEmbed && playing && (
            <iframe
              src={toEmbedUrl(videoUrl)}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid={`v2-video-embed-${number}`}
            />
          )}

          {/* Overlay UI (placeholder banner + play button when not playing) */}
          {(!hasVideo || (isEmbed && !playing)) && (
            <>
              {!hasVideo && (
                <>
                  <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-blue-300 font-semibold z-10">
                    <FileVideo className="w-3.5 h-3.5" /> Video {number} · placeholder
                  </div>
                  <div className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.2em] text-slate-300 bg-black/30 px-2 py-1 rounded z-10">
                    {lengthSec}s
                  </div>
                  <p className="absolute bottom-4 left-4 right-4 text-xs text-slate-300 italic z-10">
                    Drop the finished intro MP4 here (set <code>introVideo.videoUrl</code> in <code>/v2/videos.js</code>)
                  </p>
                </>
              )}

              {/* Click-to-play overlay */}
              {hasVideo && isEmbed && (
                <div className="absolute inset-0 cursor-pointer group z-10" onClick={handlePlay}>
                  {posterUrl && (
                    <img src={posterUrl} alt={title} className="absolute inset-0 w-full h-full object-cover" />
                  )}
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-white/95 group-hover:bg-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-slate-900 ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>
              )}

              {/* Placeholder centre play button */}
              {!hasVideo && (
                <button
                  type="button"
                  onClick={handlePlay}
                  disabled
                  className="relative w-20 h-20 mx-auto my-auto rounded-full bg-white/95 flex items-center justify-center shadow-2xl opacity-90 cursor-not-allowed"
                  data-testid={`v2-video-play-${number}`}
                  style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
                >
                  <Play className="w-8 h-8 text-slate-900 ml-1" fill="currentColor" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Script / timeline panel */}
        <div className={`p-8 ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
          <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-3 ${dark ? 'text-blue-400' : 'text-blue-600'}`}>
            Video {number}
          </p>
          <h3 className={`text-xl md:text-2xl font-medium mb-4 ${dark ? 'text-white' : 'text-slate-900'}`}>{title}</h3>
          {scriptSummary && (
            <p className={`text-sm leading-relaxed italic mb-6 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
              “{scriptSummary}”
            </p>
          )}
          {timeline.length > 0 && (
            <div>
              <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-2 ${dark ? 'text-slate-500' : 'text-slate-500'}`}>
                Timeline
              </p>
              <ul className="space-y-1 text-xs">
                {timeline.map((t) => (
                  <li key={t} className="flex gap-2">
                    <span className={`font-mono ${dark ? 'text-blue-400' : 'text-blue-600'}`}>{t.split(' — ')[0]}</span>
                    <span className={dark ? 'text-slate-400' : 'text-slate-600'}>{t.split(' — ')[1]}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoPlaceholder;
